import {
    Box,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Stack,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { Field, useFormikContext } from 'formik';
import { useState, useMemo, useEffect } from 'react';

import { useRegistrarFilter } from 'pages/resources/aors/hooks/useRegistrarFilter';

import FieldToolTip from 'components/forms/new/FieldToolTip';
import {
    descriptionStyling,
    dropDownFormStyle,
    formSelectStyles,
    formLabelStyling,
    stackSpacing,
    stackStyling,
    textBoxStyling,
} from 'components/forms/new/formStyles';

const RegistrarField = ({
    errors,
    values,
    touched,
    registrars = [],
    darkMode,
    isDisabled = false,
    validationError = { registrarID: '' },
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const { setFieldValue } = useFormikContext();

    const { selectedRegistrarId } = useRegistrarFilter(registrars);
    const selectedRegistrar = useMemo(() => {
        if (!registrars.length) return null;

        const targetId = values.registrarID || selectedRegistrarId;
        return registrars.find((r) => r.id === targetId) || null;
    }, [registrars, values.registrarID, selectedRegistrarId]);

    const baseStyles = dropDownFormStyle(darkMode);
    const filterOption = (option, inputValue) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase());

    useEffect(() => {
        if (selectedRegistrarId && !values.registrarID && selectedRegistrar) {
            setFieldValue(
                'registrarID',
                selectedRegistrarId || selectedRegistrar.id,
            );
        }
    }, [
        selectedRegistrarId,
        selectedRegistrar,
        values.registrarID,
        setFieldValue,
    ]);

    return (
        <FormControl
            isRequired
            sx={{ margin: '0 !important' }}
            isInvalid={
                Boolean(errors.registrarID || validationError.registrarID) &&
                touched.registrarID
            }
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={stackStyling(true)}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Registrar
                    </FormLabel>
                    <Text
                        sx={{
                            ...descriptionStyling,
                            color: darkMode ? 'gray.100' : '#757575',
                        }}
                        maxW={250}>
                        Select the associated Registrar
                    </Text>
                </Box>
                <Field name="registrarID">
                    {({ field, form: { setFieldValue, setTouched } }) => (
                        <Box
                            flex="1"
                            sx={{
                                paddingTop: '22px',
                                ...formSelectStyles,
                            }}>
                            <VStack width="100%" alignItems="normal">
                                <Select
                                    {...field}
                                    tabSelectsValue
                                    openMenuOnFocus
                                    placeholder="Select registrar"
                                    isDisabled={isDisabled}
                                    sx={{
                                        ...textBoxStyling,
                                        color: darkMode
                                            ? 'dark.textDark'
                                            : null,
                                        background: darkMode
                                            ? 'dark.bgDark'
                                            : null,
                                        borderColor: darkMode
                                            ? 'dark.tcap-borderGray'
                                            : null,
                                    }}
                                    value={selectedRegistrar}
                                    options={registrars}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.id}
                                    filterOption={filterOption}
                                    chakraStyles={baseStyles}
                                    onChange={(selectedOption) => {
                                        // When user manually selects, use their choice
                                        const registrarID =
                                            selectedOption?.id || '';
                                        console.log(
                                            'User selected registrar:',
                                            registrarID,
                                        );
                                        setFieldValue(
                                            'registrarID',
                                            registrarID,
                                        );
                                    }}
                                    onBlur={() => {
                                        setTouched({
                                            ...touched,
                                            registrarID: true,
                                        });
                                    }}
                                    components={{
                                        Option: ({ children, ...props }) => {
                                            const isSelected = props.isSelected;
                                            const isFocused = props.isFocused;
                                            return (
                                                <div
                                                    {...props.innerProps}
                                                    style={{
                                                        ...props.getStyles(
                                                            'option',
                                                            props,
                                                        ),
                                                        padding: '8px 12px',
                                                        borderBottom:
                                                            '1px solid ' +
                                                            (darkMode
                                                                ? '#444'
                                                                : '#eee'),
                                                        backgroundColor:
                                                            isSelected
                                                                ? 'var(--chakra-colors-brand-500)'
                                                                : isFocused
                                                                  ? darkMode
                                                                      ? 'rgba(255, 255, 255, 0.16)'
                                                                      : '#f5f5f5'
                                                                  : 'transparent',

                                                        color: isSelected
                                                            ? 'white'
                                                            : 'inherit',
                                                    }}
                                                    title={`Select ${props.data.name} registrar (${props.data.uri})`}>
                                                    <div>
                                                        <div>{children}</div>
                                                        <div
                                                            style={{
                                                                fontSize:
                                                                    '0.8em',
                                                                color: isSelected
                                                                    ? 'var(--chakra-colors-gray-100)'
                                                                    : darkMode
                                                                      ? '#888'
                                                                      : '#666',
                                                                marginTop:
                                                                    '2px',
                                                            }}>
                                                            {props.data.uri}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        },
                                    }}
                                />
                            </VStack>
                            {(errors?.registrarID ||
                                validationError?.registrarID) && (
                                <FormErrorMessage fontSize="sm" mt={1}>
                                    {errors?.registrarID ||
                                        validationError?.registrarID}
                                </FormErrorMessage>
                            )}
                        </Box>
                    )}
                </Field>

                {isHovered && (
                    <FieldToolTip
                        label="Select the registrar system that will handle this AOR registration. Each registrar manages specific regions or services."
                        ariaLabel="registrar-tip"
                        left="25px"
                        top="32px"
                    />
                )}
            </Stack>
        </FormControl>
    );
};

export default RegistrarField;
